import { useGetPackHousesQuery } from 'common/api/endpoints/schedule';
import { useGetVarietiesQuery } from 'common/api/endpoints/variety';
import { handleError } from 'common/api/utils/handleError';
import {
  CsvButton,
  CsvHeaders,
  emptyCellValue,
} from 'common/components/CsvButton/CsvButton';
import { WithLoadingOverlay } from 'common/components/LoadingSpinner';
import { TimeframeSwitcher } from 'common/components/TimeframeSwitcher/TimeframeSwitcher';
import { useTimeframeSwitcherState } from 'common/components/TimeframeSwitcher/useTimeframeSwitcherState';
import { Variety } from 'common/models';
import { Size } from 'common/models/growerBlock/size';
import { PackHouse } from 'common/models/packHouse';
import {
  CsvComboRows,
  CsvPlanHeaderKeys,
} from 'features/pick-plan-views/utils/prepareCsvPlanData';
import { Moment } from 'moment';
import React, {
  Dispatch,
  FC,
  SetStateAction,
  useEffect,
  useState,
} from 'react';
import { useHistory } from 'react-router-dom';
import { Constants } from 'utils/constants';
import { generateWeekEndString } from 'utils/helperFunction';
import { EditPickPlanModal } from '../../components/PickPlanTable/modals/EditPickPlanModal';
import { PickPlanTable } from '../../components/PickPlanTable/PickPlanTable';
import { Container, Header, Section, SwitcherCsvBtn, Title } from './styles';

export type ReqToSchedule = {
  packHouse: PackHouse;
  variety: Variety;
  market: string;
  size: Size;
  date: string;
  bins: number;
  binId: number;
};

type PlanViewProps = {
  toggleModal: Dispatch<SetStateAction<boolean>>;
  monday: Moment;
  setReqToSchedule: Dispatch<
    SetStateAction<ReqToSchedule | Record<string, never>>
  >;
  isSavingReq: boolean;
  setIsSavingReq: Dispatch<SetStateAction<boolean>>;
  setLoadingPlanCount: React.Dispatch<React.SetStateAction<number>>;
  redirectToSchedule: (binReq: ReqToSchedule | Record<string, never>) => void;
};

export const PlanViewContext = React.createContext<PlanViewProps>(null!);

export const PickPlanView: FC = () => {
  const {
    data: packHouses,
    isLoading,
    error: fetchPHError,
  } = useGetPackHousesQuery();
  const {
    data: varieties,
    isLoading: isLoadingVarieties,
    error: varietyError,
  } = useGetVarietiesQuery();
  const history = useHistory();
  const { state: week } = useTimeframeSwitcherState('pick-plan', 'week');
  const [isModalOpen, toggleModal] = useState(false);
  const [reqToSchedule, setReqToSchedule] = useState<
    ReqToSchedule | Record<string, never>
  >({});
  const [isSavingReq, setIsSavingReq] = useState(false);

  const [csvPlanData, setCsvPlanData] = useState({
    headers: [] as CsvHeaders<CsvPlanHeaderKeys>[],
    rows: {} as Record<string, CsvComboRows>,
  });
  const [formattedCsvRows, setFormattedCsvRows] = useState([] as CsvComboRows);
  const [loadingPlanCount, setLoadingPlanCount] = useState(0);

  const formatCsvRows = () => {
    const emptyRow = {
      [`${CsvPlanHeaderKeys.pHouseCode}`]: emptyCellValue,
      [`${CsvPlanHeaderKeys.variety}`]: emptyCellValue,
      [`${CsvPlanHeaderKeys.market}`]: emptyCellValue,
      [`${CsvPlanHeaderKeys.size}`]: emptyCellValue,
      [`${CsvPlanHeaderKeys.day}`]: emptyCellValue,
      [`${CsvPlanHeaderKeys.totalRequested}`]: emptyCellValue,
      [`${CsvPlanHeaderKeys.totalPicked}`]: emptyCellValue,
    };
    let newRows = [] as CsvComboRows;
    const arrRows = Object.keys(csvPlanData.rows)
      .sort((a, b) => a.localeCompare(b))
      .map(key => {
        const houseRows = csvPlanData.rows[`${key}`];
        if (
          !houseRows.findLast(
            element =>
              element[`${CsvPlanHeaderKeys.pHouseCode}`] === emptyCellValue,
          )
        ) {
          houseRows.push(emptyRow);
        }
        return houseRows;
      });
    arrRows.forEach(arr => {
      newRows = [...newRows.concat(arr)];
    });
    setFormattedCsvRows(newRows);
  };

  const redirectToSchedule = (binReq: ReqToSchedule | Record<string, never>) =>
    history.push({
      pathname: `${Constants.routes.PICK_SCHEDULE}${Constants.routes.NEW_PICK_LIST}`,
      state: binReq,
    });

  useEffect(() => {
    if (fetchPHError) handleError(fetchPHError, 'Unable to load pack houses.');
    if (varietyError) handleError(varietyError, 'Unable to load varieties.');
  }, [fetchPHError, varietyError]);

  return (
    <PlanViewContext.Provider
      value={{
        toggleModal,
        monday: week.monday,
        setReqToSchedule,
        isSavingReq,
        setIsSavingReq,
        redirectToSchedule,
        setLoadingPlanCount,
      }}
    >
      <WithLoadingOverlay isLoading={isLoading}>
        <Container>
          <Header>
            <Title>Pick Plan</Title>
            <SwitcherCsvBtn>
              <TimeframeSwitcher identifier='pick-plan' timeframe='week' />
              <CsvButton
                headers={csvPlanData.headers}
                data={formattedCsvRows}
                filename={`${generateWeekEndString(
                  week.weekdays[6],
                )}_pick_plan`}
                onClick={() => {
                  // Stop component logic if any pick plan data is being retrieved
                  if (loadingPlanCount || isSavingReq) return false;
                  formatCsvRows();
                  return true;
                }}
                isLoading={!!loadingPlanCount || isSavingReq}
              />
            </SwitcherCsvBtn>
          </Header>
          <Section>
            {packHouses?.map(house => (
              <PickPlanTable
                key={house.id}
                packHouse={house}
                varieties={varieties}
                isLoadingVarieties={isLoadingVarieties}
                week={week}
                setCsvPlanData={setCsvPlanData}
              />
            ))}
          </Section>
        </Container>
      </WithLoadingOverlay>
      <EditPickPlanModal
        redirect={() => redirectToSchedule(reqToSchedule)}
        isOpen={isModalOpen}
        closeModal={() => toggleModal(false)}
      />
    </PlanViewContext.Provider>
  );
};
